import React, { useCallback, useState } from 'react'
type Props = {
  children: React.ReactNode
  onBlur: (event: React.FocusEvent<HTMLDivElement>) => void
  onFocus: (event: React.FocusEvent<HTMLDivElement>) => void
}

/**
 * A component that tracks focus state within its container and triggers callbacks on focus/blur events
 * @param children - The child elements to render inside the focus container
 * @param onBlur - Callback fired when focus leaves the container
 * @param onFocus - Callback fired when focus enters the container
 * @returns A div that monitors focus state and triggers callbacks appropriately
 */
const FocusWithin = ({ children, onBlur, onFocus }: Props) => {
  const [isFocusWithin, setFocusWithin] = useState<boolean>(false)
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (!isFocusWithin) {
        return
      }

      if (
        !event.relatedTarget ||
        (event.relatedTarget instanceof HTMLElement &&
          !event.currentTarget.contains(event.relatedTarget))
      ) {
        if (onBlur) {
          onBlur(event)
        }

        setFocusWithin(false)
      }
    },
    [isFocusWithin, onBlur],
  )
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (isFocusWithin) {
        return
      }

      if (onFocus) {
        onFocus(event)
      }

      setFocusWithin(true)
    },
    [isFocusWithin, onFocus],
  )
  return (
    <div onBlur={handleBlur} onFocus={handleFocus}>
      {children}
    </div>
  )
}

export default React.memo<Props>(FocusWithin)
